import React from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import {Box, Button, SpaceBetween, Textarea} from '@cloudscape-design/components';
import {useStickToBottom} from 'use-stick-to-bottom';
import ChatMessage from './chat-message';
import {MessageErrorBoundary, messageResetKey} from './message-error-boundary';
import SuggestedMessages from './suggested-messages';
import {useCassandraChat} from './use-cassandra-chat';
import {DISCLAIMER_TEXT} from './cassandra-types';
import {useAuth, ServiceResource} from '../hooks/use-auth';
import {useCassandraSidePane} from './side-pane-context';
import {useRouter} from '../hooks/use-router';
import './cassandra.css';

export default function CassandraSidePane() {
    const {close, isOpen, activeChatId, setActiveChatId} = useCassandraSidePane();
    const router = useRouter();
    const visible = isOpen && router.pathname !== '/ask';
    const auth = useAuth();
    const credentials = auth.authInformation?.getEndpoints()?.NA?.[ServiceResource.ExplainabilityView]?.credentials;
    const userAlias = auth.authInformation?.email?.split('@')[0];
    const {scrollRef, contentRef, isAtBottom, scrollToBottom} = useStickToBottom();

    const {messages, status, sending, input, setInput, handleSend, handleNewChat, stop, addToolResult} = useCassandraChat({
        credentials,
        email: auth.authInformation?.email,
        activeChatId,
        setActiveChatId,
    });

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && (e.target as HTMLElement).tagName === 'TEXTAREA') {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="cassandra-side-pane" style={{display: visible ? undefined : 'none'}}>
            <div className="cassandra-side-pane-header">
                <Box variant="h3" fontSize="heading-s" fontWeight="bold">
                    Ask Excelsior
                </Box>
                <div className="cassandra-side-pane-header-actions">
                    <div className="cassandra-tooltip" data-tooltip="New conversation">
                        <Button variant="icon" iconName="add-plus" ariaLabel="New conversation" onClick={handleNewChat} />
                    </div>
                    <div className="cassandra-tooltip" data-tooltip="Open full screen">
                        <Button variant="icon" iconName="expand" ariaLabel="Open full screen" onClick={() => router.push('/ask')} />
                    </div>
                    <div className="cassandra-tooltip" data-tooltip="Close chat">
                        <Button variant="icon" iconName="close" ariaLabel="Close chat" onClick={close} />
                    </div>
                </div>
            </div>
            <div className="cassandra-side-pane-messages" ref={scrollRef} role="log">
                {messages.length === 0 && !sending && (
                    <div className="cassandra-empty-state">
                        <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Ask Excelsior" width={48} />
                        <Box variant="h3">How can I help you?</Box>
                        <Box color="text-body-secondary" textAlign="center" fontSize="body-s">
                            Ask about demand forecasts, S&OP data, or site volumes.
                        </Box>
                    </div>
                )}
                <div ref={contentRef} style={{padding: '0 12px'}}>
                    <SpaceBetween size="xs">
                        {messages.map((msg, i) => (
                            <MessageErrorBoundary key={msg.id} resetKey={messageResetKey(msg)}>
                                <ChatMessage
                                    message={msg}
                                    userAlias={userAlias}
                                    isStreaming={sending && msg.role === 'assistant' && i === messages.length - 1}
                                    onQuestionAnswer={({toolCallId, answer}) =>
                                        addToolResult({tool: 'askQuestion', toolCallId, output: answer})
                                    }
                                />
                            </MessageErrorBoundary>
                        ))}
                    </SpaceBetween>
                    {(status === 'ready' || status === 'error') && (
                        <SuggestedMessages messages={messages} onSelect={(text) => handleSend(text)} />
                    )}
                </div>
                {!isAtBottom && (
                    <div style={{position: 'sticky', bottom: 8, float: 'right', marginRight: 8}}>
                        <Button
                            variant="icon"
                            iconName="angle-down"
                            onClick={() => scrollToBottom()}
                            ariaLabel="Scroll to bottom"
                        />
                    </div>
                )}
            </div>
            <div className="cassandra-side-pane-input" onKeyDown={handleKeyDown}>
                <div className="cassandra-prompt-container">
                    <Textarea
                        value={input}
                        onChange={({detail}) => setInput(detail.value)}
                        placeholder="Type your message..."
                        disabled={sending}
                        rows={2}
                    />
                    <div className="cassandra-prompt-actions">
                        {sending ? (
                            <Button variant="icon" iconName="close" ariaLabel="Stop" onClick={() => stop?.()} />
                        ) : (
                            <Button
                                variant="primary"
                                iconName="send"
                                ariaLabel="Send message"
                                onClick={() => handleSend()}
                                disabled={!input.trim()}
                            />
                        )}
                    </div>
                </div>
                <div className="cassandra-disclaimer">{DISCLAIMER_TEXT}</div>
            </div>
        </div>
    );
}
